import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Container, Typography, Button, Backdrop, CircularProgress } from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import HoldAllocationNav from '../components/HoldAllocationNav/hold-allocation-nav'
import BucketCardsV2 from './components/BucketCardsV2/bucket-cards-v2'
import HoldFilterBarV2 from './components/HoldFilterBarV2/hold-filter-bar-v2'
import ListingSearchV2 from './components/ListingSearchV2/listing-search-v2'
import OrdersTableV2 from './components/OrdersTableV2/orders-table-v2'
import GenericSnackbar from '../../../components/GenericSnackbar/generic-snackbar'
import {
  fetchDashboard,
  fetchOrders,
  selectBucketBreakdownForSelected,
  setSelectedBucket,
  setSelectedHoldFilter,
  setSearchTerm,
} from '../hold-allocation-slice'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import { HOLD_ALLOCATION_BUCKETS } from '../../../constants/hold-allocation-constants'

function HoldAllocationListingV2() {
  const dispatch = useDispatch()
  const dashboard = useSelector((s) => s.holdAllocation.dashboard)
  const orders = useSelector((s) => s.holdAllocation.orders)
  // Chip data for the filter bar now comes from the dashboard's
  // `bucketBreakdowns` map via a selector — no separate filter-options
  // round-trip per bucket click.
  const filterOptions = useSelector(selectBucketBreakdownForSelected)
  const isFetching = useSelector((s) => s.holdAllocation.isFetching)
  const selectedBucket = useSelector((s) => s.holdAllocation.selectedBucket)
  const selectedHoldFilter = useSelector((s) => s.holdAllocation.selectedHoldFilter)
  const error = useSelector((s) => s.holdAllocation.error)

  const [mockRole, setMockRole] = useState('CUSTOMER_SERVICE')
  const [mockOrg, setMockOrg] = useState('')
  const [searchField, setSearchField] = useState('orderNo')
  const [searchValue, setSearchValue] = useState('')

  // ── Data loading ─────────────────────────────────────────────
  // Chip breakdowns are embedded in the /dashboard response
  // (bucketBreakdowns), so we only dispatch /dashboard + /orders.
  // Switching buckets re-selects from cached dashboard state without
  // another network call.
  const loadData = useCallback(
    (bucket, holdFilter, search, role, org) => {
      const params = { role, org }
      if (bucket && bucket !== HOLD_ALLOCATION_BUCKETS.ALL) params.bucket = bucket
      if (search) params.search = search
      if (holdFilter && holdFilter !== 'ALL_HOLDS') params.holdCode = holdFilter

      dispatch(fetchDashboard({ role, org }))
      dispatch(fetchOrders(params))
    },
    [dispatch],
  )

  useEffect(() => {
    loadData(selectedBucket, selectedHoldFilter, searchValue, mockRole, mockOrg)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockRole, mockOrg, selectedBucket, selectedHoldFilter])

  useEffect(() => {
    if (error) {
      dispatch(setSnackbar({ message: error, severity: 'error', action: true, autoHideDuration: 4000 }))
    }
  }, [error, dispatch])

  // ── Handlers ─────────────────────────────────────────────────
  const handleBucketClick = (bucket) => {
    dispatch(setSelectedBucket(bucket))
    dispatch(setSelectedHoldFilter('ALL_HOLDS'))
  }

  const handleHoldFilterClick = (code) => {
    dispatch(setSelectedHoldFilter(code === selectedHoldFilter ? 'ALL_HOLDS' : code))
  }

  const handleRoleChange = (role) => {
    setMockRole(role)
  }

  const handleOrgChange = (org) => {
    setMockOrg(org)
  }

  const handleSearchFieldChange = (field) => {
    setSearchField(field)
  }

  const handleSearchValueChange = (val) => {
    setSearchValue(val)
    dispatch(setSearchTerm(val))

    // Debounced search - fetch immediately for now
    const params = { role: mockRole, org: mockOrg, search: val }
    if (selectedBucket !== HOLD_ALLOCATION_BUCKETS.ALL) params.bucket = selectedBucket
    if (selectedHoldFilter !== 'ALL_HOLDS') params.holdCode = selectedHoldFilter
    dispatch(fetchOrders(params))
  }

  // ── Render ───────────────────────────────────────────────────
  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f2f5f2', minHeight: '100vh' }}>
      <HoldAllocationNav mockRole={mockRole} onRoleChange={handleRoleChange} mockOrg={mockOrg} onOrgChange={handleOrgChange} />

      <Backdrop open={isFetching} sx={{ zIndex: 9999 }}>
        <CircularProgress color='inherit' />
      </Backdrop>

      <Container maxWidth='xl' sx={{ pt: 3, pb: 6 }}>
        {/* Page header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography
            sx={{
              fontSize: '1.8rem',
              fontFamily: 'SanomatMedium, serif',
              fontWeight: 700,
              color: '#145a4b',
            }}
          >
            Allocations
          </Typography>
          <Button
            variant='outlined'
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{
              fontFamily: 'AtlasGroteskRegular, sans-serif',
              fontSize: '0.78rem',
              textTransform: 'uppercase',
              letterSpacing: 1.2,
              color: '#145a4b',
              borderColor: '#145a4b',
              borderRadius: '20px',
              px: 2.5,
              '&:hover': {
                backgroundColor: '#145a4b',
                color: '#ffffff',
              },
            }}
          >
            Open Order Report
          </Button>
        </Box>

        {/* Dashboard bucket cards */}
        <Box sx={{ mb: 3 }}>
          <BucketCardsV2
            dashboard={dashboard}
            selectedBucket={selectedBucket}
            onBucketClick={handleBucketClick}
          />
        </Box>

        {/* Hold filter chips */}
        <Box sx={{ mb: 3 }}>
          <HoldFilterBarV2
            filterOptions={filterOptions}
            selectedHoldFilter={selectedHoldFilter}
            onFilterClick={handleHoldFilterClick}
          />
        </Box>

        {/* Search bar - right aligned */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <ListingSearchV2
            searchField={searchField}
            searchValue={searchValue}
            onFieldChange={handleSearchFieldChange}
            onValueChange={handleSearchValueChange}
          />
        </Box>

        {/* Orders table */}
        <OrdersTableV2 orders={orders} isFetching={isFetching} />
      </Container>

      <GenericSnackbar />
    </Box>
  )
}

export default HoldAllocationListingV2
